import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import API from '@/config/api';

import {
	judgeResponse,
	judgesListResponse,
	type JudgesParams,
	type UseCreateJudge,
	type UseDeleteJudge,
	type UseJudge,
	type UseJudges,
	type UseToggleJudge,
	type UseUpdateJudge,
} from './Judges.schema';

const JUDGES_QUERY_KEY = ['judges'];

export const judgesKeys = {
	all: JUDGES_QUERY_KEY,
	lists: () => [...JUDGES_QUERY_KEY, 'list'] as const,
	list: (params: JudgesParams) => [...judgesKeys.lists(), params] as const,
	detail: (id: string) => [...JUDGES_QUERY_KEY, 'detail', id] as const,
};

export const useJudges = (params: JudgesParams = {}, opts: UseJudges['opts'] = {}) =>
	useQuery<UseJudges['response']>({
		...opts,
		queryKey: judgesKeys.list(params),
		queryFn: () => API.get('/judges', { params }).then((res) => judgesListResponse.parse(res.data)),
		staleTime: 30_000,
	});

export const useJudge = (id: string, opts: UseJudge['opts'] = {}) =>
	useQuery<UseJudge['response']>({
		...opts,
		queryKey: judgesKeys.detail(id),
		queryFn: () => API.get(`/judges/${id}`).then((res) => judgeResponse.parse(res.data)),
		enabled: !!id && id !== 'new',
		staleTime: 30_000,
	});

export const useCreateJudge = (opts: UseCreateJudge['opts'] = {}) => {
	const qc = useQueryClient();

	return useMutation({
		...opts,
		mutationFn: (draft) => API.post('/judges', draft).then((res) => judgeResponse.parse(res.data)),
		onSuccess: (data, vars, ctx) => {
			qc.invalidateQueries({ queryKey: judgesKeys.lists() });
			opts.onSuccess?.(data, vars, ctx);
		},
	});
};

export const useUpdateJudge = (opts: UseUpdateJudge['opts'] = {}) => {
	const qc = useQueryClient();

	return useMutation({
		...opts,
		mutationFn: ({ id, patch }) => API.patch(`/judges/${id}`, patch).then((res) => judgeResponse.parse(res.data)),
		onSuccess: (data, vars, ctx) => {
			qc.invalidateQueries({ queryKey: judgesKeys.lists() });
			qc.invalidateQueries({ queryKey: judgesKeys.detail(vars.id) });
			opts.onSuccess?.(data, vars, ctx);
		},
	});
};

export const useDeleteJudge = (opts: UseDeleteJudge['opts'] = {}) => {
	const qc = useQueryClient();

	return useMutation({
		...opts,
		mutationFn: ({ id }) => API.delete(`/judges/${id}`).then((res) => res.data as { ok: true }),
		onSuccess: (data, vars, ctx) => {
			qc.invalidateQueries({ queryKey: judgesKeys.lists() });
			opts.onSuccess?.(data, vars, ctx);
		},
	});
};

export const useToggleJudge = (opts: UseToggleJudge['opts'] = {}) => {
	const qc = useQueryClient();

	return useMutation({
		...opts,
		mutationFn: ({ id }) => API.post(`/judges/${id}/toggle`).then((res) => judgeResponse.parse(res.data)),
		onSuccess: (data, vars, ctx) => {
			qc.invalidateQueries({ queryKey: judgesKeys.lists() });
			qc.invalidateQueries({ queryKey: judgesKeys.detail(vars.id) });
			opts.onSuccess?.(data, vars, ctx);
		},
	});
};
